import { useEffect, type ReactNode } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedProps,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import Svg, { Circle } from 'react-native-svg';

const AnimatedCircle = Animated.createAnimatedComponent(Circle);

const DURATION = 1000;
const START_ANGLE_FROM = 90;
const START_ANGLE_TO = 270;
// Android FastOutSlowIn 과 동일한 cubic-bezier
const EASING = Easing.bezier(0.4, 0, 0.2, 1);

type Props = {
  size: number;
  strokeWidth: number;
  color: string;
  children: ReactNode;
};

/**
 * 스토리 UNREAD 진입 링. 마운트 시 1회 재생한다.
 * - sweep(호 길이): 0% → 100%
 * - start angle: 90° → 270°
 * 애니메이션이 끝나면 꽉 찬 원형 링 상태로 유지된다. (Android/iOS 스펙과 동일)
 */
export const StoryRing = ({ size, strokeWidth, color, children }: Props) => {
  const progress = useSharedValue(0);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  useEffect(() => {
    progress.value = withTiming(1, { duration: DURATION, easing: EASING });
  }, [progress]);

  const circleProps = useAnimatedProps(() => ({
    strokeDashoffset: circumference * (1 - progress.value),
  }));

  const rotationStyle = useAnimatedStyle(() => ({
    transform: [
      {
        rotate: `${
          START_ANGLE_FROM + (START_ANGLE_TO - START_ANGLE_FROM) * progress.value
        }deg`,
      },
    ],
  }));

  return (
    <View style={[styles.container, { width: size, height: size }]}>
      <Animated.View style={[StyleSheet.absoluteFill, rotationStyle]}>
        <Svg width={size} height={size}>
          <AnimatedCircle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={color}
            strokeWidth={strokeWidth}
            fill="none"
            strokeLinecap="round"
            strokeDasharray={circumference}
            animatedProps={circleProps}
          />
        </Svg>
      </Animated.View>
      {children}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});
